import { useEffect, useState, useCallback } from "react";
import api from "../../../utils/axiosInstance";
import { toast } from "../../../components/Toast/toast";
import { exportTable } from "../../../utils/exportTable";
import { btn } from "../../../components/Button/buttonStyles";

const HEADERS = [
  "File Code",
  "Filename",
  "Size",
  "Is Deleted from Disk",
  "Uploaded At",
  "Uploaded By",
];

const PAGE_SIZE = 20;

// Bytes -> human readable (e.g. 1.4 MB).
const formatSize = (bytes) => {
  if (bytes === null || bytes === undefined) return "—";
  if (bytes < 1024) return `${bytes} B`;
  const units = ["KB", "MB", "GB", "TB"];
  let size = bytes / 1024;
  let i = 0;
  while (size >= 1024 && i < units.length - 1) {
    size /= 1024;
    i += 1;
  }
  return `${size.toFixed(1)} ${units[i]}`;
};

const formatDate = (value) =>
  value
    ? new Date(value).toLocaleString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      })
    : "—";

// Mirrors HEADERS above, in the same order.
const EXPORT_COLUMNS = [
  { header: "File Code", value: (f) => f.file_code },
  { header: "Filename", value: (f) => f.filename },
  { header: "Size", value: (f) => formatSize(f.size) },
  { header: "Is Deleted from Disk", value: (f) => (f.is_deleted_from_disk ? "Yes" : "No") },
  { header: "Uploaded At", value: (f) => formatDate(f.uploaded_at) },
  { header: "Uploaded By", value: (f) => f.uploaded_by || "" },
];

const FilesPage = () => {
  const [files, setFiles] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [exporting, setExporting] = useState(false);

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const fetchFiles = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const { data } = await api.get(
        `/files?page=${page}&limit=${PAGE_SIZE}`,
        { withCredentials: true }
      );
      setFiles(data.data || []);
      setTotal(data.meta?.total ?? 0);
    } catch (err) {
      console.error("Failed to fetch files", err);
      setError("Failed to load files.");
    } finally {
      setLoading(false);
    }
  }, [page]);

  useEffect(() => {
    fetchFiles();
  }, [fetchFiles]);

  const handleExport = async () => {
    setExporting(true);
    try {
      // Pull every row (not just the current page) for the export.
      const { data } = await api.get(`/files?page=1&limit=100000`, {
        withCredentials: true,
      });
      exportTable("files", EXPORT_COLUMNS, data.data || []);
    } catch (err) {
      console.error("Export failed", err);
      toast.error("Failed to export files.");
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex justify-between items-center flex-wrap gap-3">
        <h1 className="text-[22px] font-semibold tracking-tight text-gray-900">Files</h1>
        <div className="flex items-center gap-2 text-sm">
          <button
            onClick={handleExport}
            disabled={exporting || total === 0}
            className={btn("secondary", "sm")}
          >
            {exporting ? "Exporting..." : "Export to Excel"}
          </button>
        </div>
      </div>

      {/* Pagination indicator */}
      <div className="flex items-center gap-3 text-sm text-gray-600">
        <button
          onClick={() => setPage((p) => Math.max(1, p - 1))}
          disabled={page <= 1 || loading}
          className={btn("secondary", "sm")}
          aria-label="Previous page"
        >
          ‹
        </button>
        <span>
          <span className="font-semibold">{page}</span> of{" "}
          <span className="font-semibold">{totalPages}</span>
        </span>
        <button
          onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
          disabled={page >= totalPages || loading}
          className={btn("secondary", "sm")}
          aria-label="Next page"
        >
          ›
        </button>
      </div>

      {error && <p className="text-danger-600 text-sm">{error}</p>}

      {/* Table */}
      <div className="w-full bg-white shadow rounded-lg overflow-hidden">
        <div className="max-h-[600px] overflow-y-auto overflow-x-auto">
          <table className="w-full table-auto border-collapse min-w-[900px]">
            <thead className="sticky top-0 z-10">
              <tr className="bg-white border-b text-[10px] tracking-widest text-gray-500 uppercase">
                {HEADERS.map((h) => (
                  <th
                    key={h}
                    className="px-3 py-2 text-center font-semibold whitespace-nowrap"
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={HEADERS.length} className="py-10 text-center">
                    <div className="inline-block animate-spin rounded-full h-8 w-8 border border-gray-300 border-t-brand-600" />
                  </td>
                </tr>
              ) : files.length === 0 ? (
                <tr>
                  <td
                    colSpan={HEADERS.length}
                    className="py-10 text-center text-gray-400 text-xs"
                  >
                    No files found.
                  </td>
                </tr>
              ) : (
                files.map((file, index) => {
                  const rowBg = index % 2 === 0 ? "bg-gray-50" : "bg-white";
                  return (
                    <tr
                      key={`${file.file_code}-${index}`}
                      className={`${rowBg} hover:bg-brand-50 transition-colors`}
                    >
                      <td className="px-3 py-2 text-xs whitespace-nowrap text-center font-medium text-gray-900">
                        {file.file_code}
                      </td>
                      <td className="px-3 py-2 text-xs whitespace-nowrap text-center text-gray-900">
                        {file.filename}
                      </td>
                      <td className="px-3 py-2 text-xs whitespace-nowrap text-center text-gray-700">
                        {formatSize(file.size)}
                      </td>
                      <td className="px-3 py-2 text-xs whitespace-nowrap text-center">
                        <span
                          className={`px-2 py-0.5 inline-block rounded-full text-[10px] font-semibold ${
                            file.is_deleted_from_disk
                              ? "bg-danger-100 text-danger-800"
                              : "bg-green-100 text-green-800"
                          }`}
                        >
                          {file.is_deleted_from_disk ? "Yes" : "No"}
                        </span>
                      </td>
                      <td className="px-3 py-2 text-xs whitespace-nowrap text-center text-gray-700">
                        {formatDate(file.uploaded_at)}
                      </td>
                      <td className="px-3 py-2 text-xs whitespace-nowrap text-center text-gray-700">
                        {file.uploaded_by || "—"}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default FilesPage;
